import { Autocomplete, Box, Button, TextField } from '@mui/material';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import ProductionDTO from '@src/adapters/dto/production/ProductionDTO';
import useFindProductionsLazy from '@src/adapters/hooks/production/useFindProductions';
import {
  addDays,
  endOfWeek,
  startOfWeek,
  subtractDays,
} from '@src/utils/dateUtils';
import { useCallback, useEffect, useState } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

interface Props {
  jobs: JobDTO[];
  onFilter(
    productionId: string | undefined,
    jobId: string | undefined,
    dateFrom: Date,
    dateTo: Date
  ): void;
}

function Filter({ jobs, onFilter }: Props) {
  const { execute: findProductions } = useFindProductionsLazy();
  const [selectedProduction, setSelectedProduction] = useState<ProductionDTO>();
  const [selectedJob, setSelectedJob] = useState<JobDTO>();
  const [productions, setProductions] = useState<ProductionDTO[]>([]);
  const [dateFrom, setDateFrom] = useState<Date>(startOfWeek());
  const [dateTo, setDateTo] = useState<Date>(endOfWeek());

  const findLists = useCallback(() => {
    findProductions().then((result) => {
      setProductions(result.productions);
    });
  }, [findProductions]);

  useEffect(() => {
    findLists();
  }, [findLists]);

  const handleNextClick = async () => {
    const newDateFrom = addDays(dateFrom, 7);
    const newDateTo = addDays(dateTo, 7);

    setDateFrom(newDateFrom);
    setDateTo(newDateTo);
    onFilter(
      selectedProduction ? selectedProduction.id : undefined,
      selectedJob ? selectedJob.id : undefined,
      newDateFrom,
      newDateTo
    );
  };

  const handlePreviousClick = async () => {
    const newDateFrom = subtractDays(dateFrom, 7);
    const newDateTo = subtractDays(dateTo, 7);
    setDateFrom(newDateFrom);
    setDateTo(newDateTo);
    onFilter(
      selectedProduction ? selectedProduction.id : undefined,
      selectedJob ? selectedJob.id : undefined,
      newDateFrom,
      newDateTo
    );
  };

  const handleProductionChange = async (production: ProductionDTO | null) => {
    if (production) {
      setSelectedProduction(production);
    } else {
      setSelectedProduction(undefined);
    }

    setSelectedJob(undefined);

    onFilter(production?.id, undefined, dateFrom, dateTo);
  };

  const handleJobChange = async (job: JobDTO | null) => {
    if (job) {
      setSelectedJob(job);
    } else {
      setSelectedJob(undefined);
    }

    onFilter(
      selectedProduction ? selectedProduction.id : undefined,
      job?.id,
      dateFrom,
      dateTo
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-end',
      }}>
      <Box sx={{ width: '250px' }}>
        <Autocomplete
          fullWidth
          disableClearable={false}
          clearOnEscape
          onChange={(event, value) => handleProductionChange(value)}
          value={selectedProduction}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          getOptionLabel={(option) => option.name || ''}
          options={productions}
          filterSelectedOptions
          sx={{
            paddingTop: '10px',
            '& .MuiAutocomplete-clearIndicator': {
              color: 'white',
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: 'white',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Production"
              variant="standard"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {params.InputProps.endAdornment}
                    </Box>
                  ),
                },
              }}
            />
          )}
        />
      </Box>
      <Box sx={{ width: '250px' }}>
        <Autocomplete
          key={selectedProduction?.id ?? 'no-production'}
          fullWidth
          onChange={(event, value) => handleJobChange(value)}
          value={selectedJob ?? null}
          disabled={!selectedProduction}
          isOptionEqualToValue={(option, value) =>
            !!option && !!value && option.id === value.id
          }
          getOptionLabel={(option) => option.role.name || ''}
          options={jobs}
          filterSelectedOptions
          sx={{
            paddingTop: '10px',
            '& .MuiAutocomplete-clearIndicator': {
              color: 'white',
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: 'white',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Jobs"
              variant="standard"
              slotProps={{
                input: {
                  ...params.InputProps,
                },
              }}
            />
          )}
        />
      </Box>
      <Button
        onClick={handlePreviousClick}
        color="secondary"
        variant="contained"
        size="medium"
        sx={{ height: '45px' }}>
        <NavigateBeforeIcon />
      </Button>
      <Button
        onClick={handleNextClick}
        color="secondary"
        variant="contained"
        size="medium"
        sx={{ height: '45px' }}>
        <NavigateNextIcon />
      </Button>
    </Box>
  );
}

export default Filter;
